import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { DatePicker } from '../../ui/date-picker'
import { Checkbox } from '../../ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Progress } from '../../ui/progress'
import { AlertCircle, ExternalLink, XCircle } from 'lucide-react'
import { requestCreator } from '../../../utilities/requests'
import useAuth from '../../../utilities/hooks/useAuth'
import useFacility from '../../../utilities/hooks/useFacility'
import useStudentNameSearch from '../../../utilities/hooks/useStudentNameSearch'
import StudentDuplicateWarning from '../../leevo_ui/StudentDuplicateWarning/StudentDuplicateWarning'

const { post: saveStudent } = requestCreator()

function StudentRegistrationWizard({ onStudentAdded, onCancel }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const { facility_id } = useParams()
  const { currentUser } = useAuth()
  const { facility } = useFacility()
  const [selectedStudent, setSelectedStudent] = useState(null)
  const { matches, searchByName, clearMatches } = useStudentNameSearch(facility_id)

  const requiresAddress = facility?.curriculums?.[0]?.requiresAddress
  const requiresBirthdate = facility?.curriculums?.[0]?.requiresBirthdate
  const requiresGender = facility?.curriculums?.[0]?.requiresGender
  const requiresLtsNumber = facility?.curriculums?.some((c) => c.requiresLtsNumber && c.sport === 'skating')
  const providesFreeSkateRentals = facility?.providesFreeRentalSkates && facility?.supportsSkating

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
    trigger
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      preferredName: '',
      gender: 'unspecified',
      birthdate: '',
      ltsNumber: '',
      skillLevel: '',
      addressLine1: '',
      addressLine2: '',
      addressCity: '',
      addressState: '',
      addressPostCode: '',
      needsSkates: false,
      skateType: '',
      skateSize: ''
    }
  })

  const needsSkates = watch('needsSkates')
  const ltsNumber = watch('ltsNumber')

  const isNextDisabled = currentStep === 2 && requiresLtsNumber && (ltsNumber?.trim().length ?? 0) < 6

  const totalSteps = 3
  const progressPercentage = (currentStep / totalSteps) * 100

  async function handleNext() {
    if (selectedStudent) {
      onCancel()
      return
    }

    let fieldsToValidate = []

    if (currentStep === 1) {
      fieldsToValidate = ['firstName', 'lastName']
      if (requiresGender) fieldsToValidate.push('gender')
      if (requiresBirthdate) fieldsToValidate.push('birthdate')
    } else if (currentStep === 2) {
      if (requiresLtsNumber) {
        fieldsToValidate = ['ltsNumber']
      }
      if (requiresAddress) {
        fieldsToValidate.push('addressLine1', 'addressCity', 'addressState', 'addressPostCode')
      }
    }

    const isValid = await trigger(fieldsToValidate)

    if (isValid) {
      setCurrentStep(currentStep + 1)
      setError(null)
    }
  }

  function handleBack() {
    setCurrentStep(currentStep - 1)
    setError(null)
  }

  async function onSubmit(data) {
    if (selectedStudent) {
      onCancel()
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const fullName = [data.firstName?.trim(), data.lastName?.trim()].filter(Boolean).join(' ')
      let formattedBirthdate = data.birthdate
      if (data.birthdate instanceof Date) {
        const year = data.birthdate.getFullYear()
        const month = String(data.birthdate.getMonth() + 1).padStart(2, '0')
        const day = String(data.birthdate.getDate()).padStart(2, '0')
        formattedBirthdate = `${year}-${month}-${day}`
      }

      const createParams = {
        user: {
          fullName,
          preferredName: data.preferredName?.trim() || data.firstName?.trim() || null,
          gender: data.gender,
          birthdate: formattedBirthdate || null,
          ltsNumber: data.ltsNumber || null,
          skillLevel: data.skillLevel || null,
          guardians: [{ email: currentUser.email }]
        }
      }

      if (requiresAddress && (data.addressLine1 || data.addressCity)) {
        createParams.user.address = {
          line1: data.addressLine1 || '',
          line2: data.addressLine2 || '',
          city: data.addressCity || '',
          state: data.addressState || '',
          postCode: data.addressPostCode || ''
        }
      }

      if (providesFreeSkateRentals && data.needsSkates) {
        createParams.user.skateRentalAttributes = {
          active: true,
          skateType: data.skateType,
          size: parseFloat(data.skateSize)
        }
      }

      const response = await saveStudent(`/api/facilities/${facility_id}/users`, createParams)
      onStudentAdded(response)
    } catch (err) {
      setError(
        err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'Something went wrong. Please try again.'
      )
      setIsSubmitting(false)
    }
  }

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-Binary' },
    { value: 'unspecified', label: 'Prefer not to say' }
  ]

  const skateTypeOptions = [
    { value: 'youth_figure', label: 'Youth Figure' },
    { value: 'men_figure', label: "Men's Figure" },
    { value: 'women_figure', label: "Women's Figure" },
    { value: 'men_hockey', label: "Men's Hockey" },
    { value: 'women_hockey', label: "Women's Hockey" },
    { value: 'youth_hockey', label: 'Youth Hockey' }
  ]

  const { onBlur: rhfLastNameBlur, ...restLastName } = register('lastName', {
    required: 'Last name is required'
  })
  const { onChange: rhfFirstNameChange, ...restFirstName } = register('firstName', {
    required: 'First name is required'
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle>Add Student</CardTitle>
          <CardDescription>
            Step {currentStep} of {totalSteps}
          </CardDescription>
          <Progress value={progressPercentage} className="mt-2" />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      placeholder="Jane"
                      autoFocus
                      {...restFirstName}
                      onChange={(e) => {
                        rhfFirstNameChange(e)
                        if (selectedStudent) {
                          setSelectedStudent(null)
                          clearMatches()
                        }
                      }}
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                    {errors.firstName && <p className="text-sm text-red-600">{errors.firstName.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="Smith"
                      {...restLastName}
                      onBlur={(e) => {
                        rhfLastNameBlur(e)
                        if (!selectedStudent) {
                          const firstName = getValues('firstName')?.trim()
                          const lastName = e.target.value?.trim()
                          if (firstName && lastName) searchByName(`${firstName} ${lastName}`)
                        }
                      }}
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                    {errors.lastName && <p className="text-sm text-red-600">{errors.lastName.message}</p>}
                  </div>
                </div>

                {selectedStudent ? (
                  <div className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-amber-800">
                          ✓ Using existing student: {selectedStudent.full_name}
                        </p>
                        <p className="text-xs text-amber-700">Their existing profile will be used as-is.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStudent(null)
                          clearMatches()
                          setValue('firstName', '')
                          setValue('lastName', '')
                        }}
                        className="ml-3 text-xs text-amber-700 underline hover:text-amber-900"
                      >
                        Start over
                      </button>
                    </div>
                  </div>
                ) : (
                  matches.length > 0 && (
                    <StudentDuplicateWarning
                      matches={matches}
                      onSelectStudent={(student) => {
                        const parts = student.full_name.split(' ')
                        setValue('firstName', parts[0] || '')
                        setValue('lastName', parts.slice(1).join(' ') || '')
                        if (student.birthdate) setValue('birthdate', new Date(student.birthdate + 'T00:00:00'))
                        setSelectedStudent(student)
                        clearMatches()
                      }}
                      onDismiss={clearMatches}
                    />
                  )
                )}

                <div className="space-y-2">
                  <Label htmlFor="preferredName">Preferred Name</Label>
                  <Input
                    id="preferredName"
                    placeholder="Jane"
                    {...register('preferredName')}
                    className={errors.preferredName ? 'border-red-500' : ''}
                  />
                  {errors.preferredName && <p className="text-sm text-red-600">{errors.preferredName.message}</p>}
                </div>

                {requiresGender && (
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            {genderOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                )}

                {requiresBirthdate && (
                  <div className="space-y-2">
                    <Label htmlFor="birthdate">Birthdate</Label>
                    <Controller
                      name="birthdate"
                      control={control}
                      rules={{
                        required: requiresBirthdate ? 'Birthdate is required' : false
                      }}
                      render={({ field }) => (
                        <DatePicker value={field.value} onChange={field.onChange} placeholder="Select birthdate" />
                      )}
                    />
                    {errors.birthdate && <p className="text-sm text-red-600">{errors.birthdate.message}</p>}
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Membership & Address</h3>

                {requiresLtsNumber && (
                  <>
                    <div className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <div className="space-y-2">
                          <h4 className="font-semibold text-purple-900">Enter Your Student&apos;s LTS Number</h4>
                          <p className="text-sm text-purple-800">
                            Each student needs their own LTS USA or USFSA membership number to register for classes.
                            Parents are responsible for registering their student with LTS USA.
                          </p>
                          <div className="pt-2 space-y-2 text-sm text-purple-700">
                            <p>
                              <strong>Need to register?</strong> Visit{' '}
                              <a
                                href="https://www.learntoskateusa.com/registration/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:text-purple-800 underline font-medium"
                              >
                                learntoskateusa.com
                              </a>{' '}
                              to get your student&apos;s LTS number.
                            </p>
                            <p>
                              <strong>Already registered?</strong> Enter the membership number below. You can find it in
                              your LTS USA account or on your membership confirmation email.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ltsNumber">
                        LTS/USFSA Membership Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="ltsNumber"
                        placeholder="e.g., 1234567"
                        {...register('ltsNumber', {
                          required: requiresLtsNumber ? 'An LTS membership number is required to continue.' : false,
                          minLength: requiresLtsNumber
                            ? {
                                value: 6,
                                message: 'An LTS membership number must be at least 6 digits.'
                              }
                            : undefined
                        })}
                        className={errors.ltsNumber ? 'border-red-500' : ''}
                      />
                      <p className="text-xs text-gray-500">
                        The 6–8 digit number from your LTS USA account or membership confirmation email.
                      </p>
                      {errors.ltsNumber && (
                        <div className="flex items-start gap-3 p-3 rounded-md border border-red-300 bg-red-50">
                          <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="space-y-1 text-sm">
                            <p className="font-medium text-red-800">{errors.ltsNumber.message}</p>
                            <p className="text-red-700">
                              This number is required for all students registering for skating classes and cannot be
                              skipped. If your student is not yet registered with Learn to Skate USA, you must do that
                              first before completing this registration.
                            </p>
                            <a
                              href="https://www.learntoskateusa.com/registration/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-red-800 underline font-medium hover:text-red-900"
                            >
                              Register at Learn to Skate USA
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {requiresAddress && (
                  <div className="space-y-3 pt-4 border-t">
                    <h4 className="font-medium">Address Information</h4>

                    <div className="space-y-2">
                      <Label htmlFor="addressLine1">
                        Address Line 1 {requiresAddress && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="addressLine1"
                        placeholder="123 Main Street"
                        {...register('addressLine1', {
                          required: requiresAddress ? 'Address is required' : false
                        })}
                        className={errors.addressLine1 ? 'border-red-500' : ''}
                      />
                      {errors.addressLine1 && <p className="text-sm text-red-600">{errors.addressLine1.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="addressLine2">Address Line 2</Label>
                      <Input id="addressLine2" placeholder="Apt 4B" {...register('addressLine2')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="addressCity">
                          City {requiresAddress && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id="addressCity"
                          placeholder="City"
                          {...register('addressCity', {
                            required: requiresAddress ? 'City is required' : false
                          })}
                          className={errors.addressCity ? 'border-red-500' : ''}
                        />
                        {errors.addressCity && <p className="text-sm text-red-600">{errors.addressCity.message}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="addressState">
                          State {requiresAddress && <span className="text-red-500">*</span>}
                        </Label>
                        <Input
                          id="addressState"
                          placeholder="State"
                          {...register('addressState', {
                            required: requiresAddress ? 'State is required' : false
                          })}
                          className={errors.addressState ? 'border-red-500' : ''}
                        />
                        {errors.addressState && <p className="text-sm text-red-600">{errors.addressState.message}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="addressPostCode">
                        ZIP Code {requiresAddress && <span className="text-red-500">*</span>}
                      </Label>
                      <Input
                        id="addressPostCode"
                        placeholder="12345"
                        {...register('addressPostCode', {
                          required: requiresAddress ? 'ZIP code is required' : false
                        })}
                        className={errors.addressPostCode ? 'border-red-500' : ''}
                      />
                      {errors.addressPostCode && (
                        <p className="text-sm text-red-600">{errors.addressPostCode.message}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Additional Information</h3>

                {providesFreeSkateRentals && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Controller
                        name="needsSkates"
                        control={control}
                        render={({ field }) => (
                          <Checkbox id="needsSkates" checked={field.value} onCheckedChange={field.onChange} />
                        )}
                      />
                      <Label htmlFor="needsSkates" className="font-normal cursor-pointer">
                        This student needs rental skates
                      </Label>
                    </div>

                    {needsSkates && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="skateType">
                            Skate Type <span className="text-red-500">*</span>
                          </Label>
                          <Controller
                            name="skateType"
                            control={control}
                            rules={{
                              required: needsSkates ? 'Skate type is required' : false
                            }}
                            render={({ field }) => (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger className={errors.skateType ? 'border-red-500' : ''}>
                                  <SelectValue placeholder="Select skate type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {skateTypeOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                          {errors.skateType && <p className="text-sm text-red-600">{errors.skateType.message}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="skateSize">
                            Skate Size <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="skateSize"
                            type="number"
                            step="0.5"
                            min="1"
                            max="20"
                            placeholder="10.5"
                            {...register('skateSize', {
                              required: needsSkates ? 'Skate size is required' : false
                            })}
                            className={errors.skateSize ? 'border-red-500' : ''}
                          />
                          {errors.skateSize && <p className="text-sm text-red-600">{errors.skateSize.message}</p>}
                          <p className="text-sm text-gray-500">You can specify half sizes (e.g., 10.5)</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="p-4 rounded-md bg-gray-50">
                  <p className="text-sm text-gray-700">
                    Review the information above. You can go back to make changes or click &quot;Add Student&quot; to
                    continue.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6 pt-6 border-t">
              <div>
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                    Back
                  </Button>
                )}
                {currentStep === 1 && onCancel && (
                  <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                  </Button>
                )}
              </div>

              <div>
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {selectedStudent ? `Continue with ${selectedStudent.full_name} →` : 'Next'}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting} className="bg-purple-600 hover:bg-purple-700">
                    {isSubmitting ? (
                      <>
                        <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Adding...
                      </>
                    ) : selectedStudent ? (
                      `Continue with ${selectedStudent.full_name} →`
                    ) : (
                      'Add Student'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

StudentRegistrationWizard.propTypes = {
  onStudentAdded: PropTypes.func.isRequired,
  onCancel: PropTypes.func
}

export default StudentRegistrationWizard
